/* eslint-disable max-lines */
import FiltersSideBar from '../FiltersSideBar';
import Accordion from '../base/Accordion';
import Border from '../base/Border';
import ChipsList from '../base/ChipsList';
import Icon from '../base/Icon';
import TableSkeleton from '../base/Skeletons';
import Table from '../base/Table';
import Text from '../base/Text';
import { Flex, Padding } from '../base/layout-components';
import { horizontalScroller } from '../base/scroller';
import FiltersPanel from './FiltersPanel';
import {
  WrappedAutocomplete,
  renderCheckboxes,
  renderRadioButtons,
  updateInitialSearch,
  AUTOCOMPLETE_TYPES,
  formatDatetimeForChips,
  setQueryFilter
} from './helpers';
import useFilteredColumns from './useFilteredColumns';
import { FormGroup } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import { getColor } from 'common/constants/_colors';
import {
  TRACKING,
  SEARCH_DATE_FORMAT,
  TEAM_TYPES,
  VIEW_PATH_KEYS
} from 'common/constants/constants';
import { SPACES } from 'common/constants/sizes';
import * as LighthouseService from 'common/services/lighthouse.service';
import * as TrackingService from 'common/services/tracking.service';
import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import xor from 'lodash/xor';
import difference from 'lodash/difference';
import intersectionBy from 'lodash/intersectionBy';
import flatten from 'lodash/flatten';
import keyBy from 'lodash/keyBy';
import startCase from 'lodash/startCase';
import isObject from 'lodash/isObject';
import startsWith from 'lodash/startsWith';
import findIndex from 'lodash/findIndex';
import QueryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { individualContributorsSelector } from 'state/selectors/contributors';
import {
  currentIterationSelector,
  getLatestIterationEndDateSelector,
  getLatestIterationSelector
} from 'state/selectors/iterations-picker';
import EmptyData from 'components/EmptyData';
import styled from 'styled-components';
import TreeTeamsDropdown from 'components/teams/TreeTeamsDropdown/TreeTeamsDropdown';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import { useHistory, useLocation } from 'react-router-dom';
import { parseNumericStr } from 'common/utils/string.utils';
import { COMPONENT_TYPE } from './consts';
import { DATE_FILTERS, DateFilter, type DateFilterValue } from './DateFilter';
import { useDateFilterValue } from './useDateFilterValue';
import { InvalidDateRangePlaceholder } from './InvalidDateRangePlaceholder';
import { useDisableGetData } from './useDisableGetData';
import { getIsGitScopeByKey } from 'components/TeamGitScopeBanner/utils';
import { LBButton } from '@linearb/buzz';

const PAGINATION_DEFAULT = 25;

const AccordionTitle = styled(Text)`
  text-transform: uppercase;
`;

const MainSection = styled.div`
  ${horizontalScroller()};
  height: 100%;
  flex: 100;
`;

const ClickAblePadding = styled(Padding)`
  cursor: pointer;
`;

const PaddingWithBackground = styled(ClickAblePadding)`
  background-color: ${getColor('chambray', 0.1)};
`;

const FullWidthWithSpace = styled(Flex)`
  max-width: calc(100% - 2rem);
`;

declare global {
  interface Window {
    TrackJS: {
      // TODO: type this better and move to different location
      track(param1: any, param2: any): void;
    };
  }
}
// eslint-disable-next-line max-lines-per-function
const GridChipFilterContainer = ({
  selectedTeam,
  columns,
  defaultColumns,
  skeletonColumns,
  showSkeletonColumnsHead,
  getData,
  gitScopeToggle = null,
  isExternalLoading = false,
  data = { items: [], summary: {} },
  tableData,
  getReqFilterBody,
  filtersConfig,
  filterChipLabelMap,
  filtersGroupMap,
  additionalFiltersGroupMap = [],
  pageTag,
  pageTabTag,
  getSummaryRow,
  showOnlyAdditionalChips = false,
  additionalCustomChips = [],
  disableChips = false,
  loadingInitState = false,
  refreshOnDependencies = [],
  preTableSlot = null,
  postFiltersSlot = null,
  lineHighlightOnHover = false,
  sendAllContributorsTeamId = false,
  teamsFilterKey = 'teams',
  emptyResultSlot = (
    <>
      {/* @ts-expect-error */}
      <EmptyData
        title='All filtered out'
        info={
          <>
            Sorry, that combination has no results.
            <br />
            Please try different filters
          </>
        }
      />
    </>
  ),
  filterBySelectedTeam = true,
  disableDateFilterLimit: disableDateFilterLimitProp = false
}) => {
  const history = useHistory();
  const location = useLocation();
  const search = QueryString.parse(location.search);
  const isDrilldown = Boolean(search.drilldown);
  const disableDateFilterLimit = isDrilldown || disableDateFilterLimitProp;
  const filteredColumns = useFilteredColumns({
    columns,
    defaultColumns,
    history,
    location
  });
  const page = search.page ? Number(search.page) : 0;
  const pageSize = search.pageSize
    ? Number(search.pageSize)
    : PAGINATION_DEFAULT;

  const [orderByField, orderByOrder] = (
    get(search, 'order_by', '') as string
  ).split(',');

  const setQueryParams = (obj, append = true) => {
    const search = QueryString.parse(window.location.search);
    const newSearchString = `?${QueryString.stringify(
      Object.assign({}, append ? search : {}, obj)
    )}`;
    history.replace({
      pathname: location.pathname,
      search: newSearchString
    });
  };

  function handleDateFilterChange(
    { filterName: oldFilterName }: DateFilterValue,
    { filterName, range }: DateFilterValue
  ) {
    let newDateRange = `${range.from},${range.to}`;

    if (!range.to || !range.from) {
      newDateRange = '';
    }
    setQueryFilter({
      [oldFilterName]: undefined,
      [filterName]: newDateRange
    });
  }

  const extractFromSearch = (key, defaultValue?: any) => {
    const search = QueryString.parse(location.search);

    return get(search, key, defaultValue);
  };

  const doesSearchMatchState = state => {
    const search = QueryString.parse(location.search);
    const filtersFromSearch = pickBy(
      search,
      (_value, key) =>
        [...Object.keys(filtersConfig), ...Object.keys(state)].includes(key) &&
        key !== 'order_by'
    );
    const stateWithoutOrder = pickBy(
      state,
      (_value, key) => key !== 'order_by'
    );

    return isEqual(filtersFromSearch, stateWithoutOrder);
  };

  const [prevSearch, setPrevSearch] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(loadingInitState);
  const [chips, setChips] = useState([]);
  const [reqFilterBody, setReqFilterBody] = useState<Record<string, any>>(
    getReqFilterBody()
  );
  const [isUncheckDefault, setIsUncheckDefault] = useState(false);

  const onSort = (column, order) => {
    setQueryParams(
      {
        order_by: `${column},${order}`
      },
      true
    );
  };

  const onPageSizeChange = event =>
    setQueryParams({ pageSize: event.target.value });
  const onPageChange = (_event, page) => setQueryParams({ page });

  const onListFilterChange = (filterGroupName, values) => {
    // if filter by team and no team passed - filter by selectedTeam
    // for example: if a child of Group Team is selected and removing him from chip - should filter by selectedTeam
    if (
      filterBySelectedTeam &&
      filterGroupName === teamsFilterKey &&
      selectedTeam &&
      values.length === 0
    ) {
      values = [selectedTeam.id];
    }

    if (!values) {
      return setQueryParams(omit(search, filterGroupName), false);
    }
    setQueryParams(
      {
        [filterGroupName]: values.length ? values.join(',') : values
      },
      true
    );
  };

  const onDateFilterChange = (filterGroupName, { to, from }) => {
    const formattedFrom = from ? from.format(SEARCH_DATE_FORMAT) : null;
    const formattedTo = to ? to.format(SEARCH_DATE_FORMAT) : null;

    if (!formattedFrom && !formattedTo) {
      const search = QueryString.parse(location.search);
      return setQueryParams(omit(search, filterGroupName), false);
    }

    setQueryParams(
      {
        [filterGroupName]: `${[formattedFrom, formattedTo].join(',')}`
      },
      true
    );
  };

  const onContRepoTableCellClick =
    (
      filterGroupName,
      listOfOptions = [],
      nameGetter = item => item,
      overrideSearchFunction = null
    ) =>
    cellValue => {
      if (!Array.isArray(listOfOptions)) {
        window.TrackJS &&
          window.TrackJS.track(
            'listOfOptions is not array. filterGroupName:',
            filterGroupName
          );
        return;
      }

      const id = get(
        overrideSearchFunction
          ? overrideSearchFunction(cellValue)
          : listOfOptions.find(({ name }) => name === nameGetter(cellValue)),
        'id',
        null
      );
      if (!id) return false;

      const locationValue = extractFromSearch(filterGroupName);
      const values = locationValue ? locationValue.split(',') : [];

      onListFilterChange(filterGroupName, xor([id.toString()], values));
    };

  const handleCheckboxFilterChange = (
    filterGroupName: string,
    filterName: any,
    isChecked: boolean,
    options: Record<string, any> = {}
  ) => {
    const search = QueryString.parse(location.search);

    const currentSelectedFilterString = get(search, filterGroupName, null);
    const currentSelectedFilters = currentSelectedFilterString
      ? currentSelectedFilterString.split(',')
      : [];

    const selectedFilters = isChecked
      ? options.denyOthers
        ? [filterName]
        : [...currentSelectedFilters, filterName]
      : difference(currentSelectedFilters, [filterName]);

    if (!selectedFilters.length) {
      return setQueryParams(omit(search, filterGroupName), false);
    }

    setQueryParams(
      {
        [filterGroupName]: selectedFilters.join(',')
      },
      true
    );
  };

  const handleRadioButtonGroupFilterChange = (filterGroupName, value?: any) => {
    if (!value) {
      const search = QueryString.parse(location.search);

      return setQueryParams(omit(search, filterGroupName), false);
    }

    setQueryParams(
      {
        [filterGroupName]: value
      },
      true
    );
  };

  const handlePredefinedCheckboxFilterChange = filterName => {
    const searchState = get(
      filtersConfig,
      `['${filterName}'].searchState`,
      null
    );

    if (searchState) {
      setQueryParams(searchState, false);
    }
  };

  const getOptions = item => {
    const {
      title,
      name,
      placeholderName,
      addAvatars,
      multiple,
      isDisableChildrenOfSelectedParent = true
    } = item;
    const values = extractFromSearch(name, '')
      .split(',')
      .map(val => parseNumericStr(val));

    const options = get(filtersConfig, `[${name}].options`, []);
    const autoCompleteProps = get(
      filtersConfig,
      `[${name}].autoCompleteProps`,
      {}
    );
    const getAvatar = get(filtersConfig, `[${name}].getAvatar`);
    const selectedOptions = flatten(
      intersectionBy(
        options,
        values.map(id => ({ id })),
        'id'
      )
    ).map(({ id }: any) => id);

    const optionsById = keyBy(options, 'id');
    const autocompleteFilterProps = {
      title,
      name,
      placeholderName,
      values,
      options,
      onChange: onListFilterChange,
      optionsById,
      selectedOptions,
      getAvatar,
      addAvatars,
      autoCompleteProps,
      multiple
    };

    // TreeTeams filter is shown only if selected team is All Teams || selected team is Parent and FF is true
    const treeFilterProps = {
      isFilterTreeBySelectedTeam: !!selectedTeam && filterBySelectedTeam, // when hide personal data - should show all teams
      isDisableChildrenOfSelectedParent, // true for Activity pages; false for Teams pages
      isAllowNoDefault: !!selectedTeam && isDisableChildrenOfSelectedParent, // allow TreeTeams to have no default (no team is checked; allows selecting teams children)
      isUncheckDefault,
      name,
      options,
      onChange: selected => {
        // if selected team not passed - force uncheck default team in TreeTeamsDropdown
        setIsUncheckDefault(
          !selected.length &&
            !!selectedTeam &&
            isDisableChildrenOfSelectedParent
        ); // no selected team passed and selectedTeam is not all teams
        onListFilterChange(name, selected.map(({ id }) => id).filter(Boolean));
      },
      selectedTeamIds: selectedOptions,
      multiple
    };

    return { autocompleteFilterProps, treeFilterProps };
  };

  const renderRadioFilters = ({
    title,
    names,
    defaultIsOpen,
    articleId
  }: any) => ({
    title,
    defaultIsOpen,
    filters: () => {
      return (
        <Padding t='small' b='small'>
          <Accordion
            defaultIsOpen={defaultIsOpen}
            articleId={articleId}
            titleComponent={<AccordionTitle>{startCase(title)}</AccordionTitle>}
          >
            {names.map(({ name, label }, idx) => {
              const value = extractFromSearch(name);
              const { options } = filtersConfig[name];

              return renderRadioButtons({
                name,
                label,
                value,
                options,
                onChange: handleRadioButtonGroupFilterChange,
                idx
              });
            })}

            <Padding padding='small'>
              <LBButton
                onClick={() => {
                  const search = QueryString.parse(location.search);
                  names.forEach(({ name }) => delete search[name]);
                  return setQueryParams(search, false);
                }}
              >
                Clear
              </LBButton>
            </Padding>
          </Accordion>
        </Padding>
      );
    }
  });

  const renderCheckboxFilters = ({
    title,
    categories = [],
    defaultIsOpen
  }: any) => ({
    title,
    filters: () => {
      return (
        <Padding t='small' b='small'>
          <Accordion
            defaultIsOpen={defaultIsOpen}
            titleComponent={<AccordionTitle>{startCase(title)}</AccordionTitle>}
          >
            <FormGroup>
              {categories.map(({ name, articleId }) => {
                const filterOptions = get(
                  filtersConfig,
                  `[${name}].options`,
                  []
                );
                let options = filterOptions
                  .map(opt => (isObject(opt) ? opt : { key: opt }))
                  .map(opt => ({
                    ...opt,
                    isChecked: extractFromSearch(name, '').includes(opt.key)
                  }));
                if (options.find(opt => opt.isChecked && opt.denyOthers)) {
                  options = options.map(o =>
                    o.denyOthers && o.isChecked
                      ? o
                      : { ...o, isChecked: false, disabled: true }
                  );
                }
                const onChange = (filterName, isChecked, options) =>
                  handleCheckboxFilterChange(
                    name,
                    filterName,
                    isChecked,
                    options
                  );
                return renderCheckboxes({
                  name,
                  options: keyBy(options, 'key'),
                  onChange,
                  articleId
                });
              })}
            </FormGroup>
          </Accordion>
        </Padding>
      );
    }
  });

  const renderAutocompleteFilters = ({ items, defaultIsOpen, title }: any) => ({
    title,
    defaultIsOpen: true,
    filters: () => {
      return (
        <Padding t='small'>
          <Accordion
            defaultIsOpen={defaultIsOpen}
            titleComponent={<AccordionTitle>{title}</AccordionTitle>}
          >
            {items.map((item, i) => {
              const { name } = item;
              const { autocompleteFilterProps } = getOptions(item);
              return (
                <Padding key={name + i} t='small'>
                  <WrappedAutocomplete
                    {...autocompleteFilterProps}
                    title={title}
                  />
                </Padding>
              );
            })}
          </Accordion>
        </Padding>
      );
    }
  });

  const renderAutocompleteByType = ({ items, defaultIsOpen, title }: any) => ({
    title,
    defaultIsOpen: true,
    filters: () => {
      return (
        <Padding t='small'>
          <Accordion
            defaultIsOpen={defaultIsOpen}
            titleComponent={<AccordionTitle>{title}</AccordionTitle>}
          >
            {items.map((item, i) => {
              const { name, type } = item;
              const { autocompleteFilterProps, treeFilterProps } =
                getOptions(item);
              return (
                <Padding key={name + i} t='small'>
                  {type === AUTOCOMPLETE_TYPES.TREE_TEAMS_DROPDOWN ? (
                    <TreeTeamsDropdown
                      key='TeamsDropdown'
                      data-testid='multilevel-dropdown'
                      menuWidth='23rem'
                      menuWidthExpanded='46rem'
                      numOfMenus={2}
                      {...treeFilterProps}
                    />
                  ) : (
                    <WrappedAutocomplete {...autocompleteFilterProps} />
                  )}
                </Padding>
              );
            })}
          </Accordion>
        </Padding>
      );
    }
  });

  const renderCustomFilter = ({ CustomFilter, defaultIsOpen, title }: any) => ({
    title,
    defaultIsOpen: true,
    filters: () => {
      return (
        <Padding t='small' b='small'>
          <Accordion
            defaultIsOpen={defaultIsOpen}
            titleComponent={<AccordionTitle>{title}</AccordionTitle>}
          >
            {CustomFilter}
          </Accordion>
        </Padding>
      );
    }
  });

  const renderPredefined = ({
    title,
    itemsWeight = 300,
    defaultIsOpen,
    page = '',
    names
  }: any) => ({
    title,
    defaultIsOpen,
    filters: () => {
      return (
        <div>
          <FormGroup>
            {names.map((pct, idx) => {
              const {
                searchState,
                badge,
                articleId,
                subTitle,
                amplitudeClickType
              } = get(filtersConfig, pct, {});
              const value = doesSearchMatchState(searchState);

              const StyledPadding = value
                ? PaddingWithBackground
                : ClickAblePadding;

              return (
                <Border b={`1px solid ${getColor('gray', 0.2)}`} key={idx}>
                  <StyledPadding
                    t='tiny'
                    b='small'
                    l='small'
                    onClick={() => {
                      amplitudeClickType &&
                        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                          page: TRACKING.PAGES.ACTIVITY,
                          page_tab: page,
                          click_type: amplitudeClickType
                        });
                      handlePredefinedCheckboxFilterChange(pct);
                    }}
                  >
                    <Flex
                      direction='row'
                      justify='space-between'
                      width='100%'
                      key={idx}
                    >
                      <Flex direction='column' grow='1' width='100%'>
                        <Flex
                          items='center'
                          justify='space-between'
                          width='100%'
                        >
                          <FullWidthWithSpace items='flex-end' grow={1}>
                            <Tooltip title={pct}>
                              <Text
                                pt={SPACES.small}
                                pb={SPACES.tiny}
                                size='medium'
                                ellipsisAfter='calc(100% - 4rem)'
                                weight={itemsWeight}
                                cursor='pointer'
                              >
                                {pct}
                              </Text>
                            </Tooltip>

                            <Padding l='x-small'>
                              {articleId && (
                                <LBButton
                                  onClick={() =>
                                    LighthouseService.navigateArticle(articleId)
                                  }
                                  size='icon'
                                  variant='ghost'
                                >
                                  <Icon
                                    name='helpicon'
                                    size='smaller'
                                    color='nepal'
                                  />
                                </LBButton>
                              )}
                            </Padding>
                          </FullWidthWithSpace>

                          <Flex items='center'>
                            {(badge || badge === 0) && (
                              <Padding r='small' t='small'>
                                <Text
                                  size='medium'
                                  weight='500'
                                  color='darkGray'
                                >
                                  {badge}
                                </Text>
                              </Padding>
                            )}
                          </Flex>
                        </Flex>

                        <Flex pt='x-x-small'>
                          <Text size='medium' color='shuttleGray'>
                            {subTitle}
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  </StyledPadding>
                </Border>
              );
            })}
          </FormGroup>
        </div>
      );
    }
  });

  const dateFilterToChip = (value, label, key) => {
    const { from, to } = value;
    if (!from && !to) return [];

    const formattedFrom = formatDatetimeForChips(from);
    const formattedTo = formatDatetimeForChips(to);

    return [
      {
        label: `${label} ${
          from && to
            ? `between ${formattedFrom} and ${formattedTo}`
            : from
            ? `after ${formattedFrom}`
            : to
            ? `before ${formattedTo}`
            : null
        }`,
        color: 'lb',
        textColor: getColor('shark'),
        size: 'medium',
        variant: 'outlined',
        dataCy: 'date-filter',
        onDelete: () => {
          onDateFilterChange(key, { to: null, from: null });
        }
      }
    ];
  };

  const parseValueForChip = value => {
    return startsWith(value, '>') ? `${value.match(/\d+/)[0]}+` : value;
  };

  const compareRadioToChip = (value, label, key) => {
    const { options } = get(filtersConfig, key, []);

    return {
      label: `${label}: ${get(
        options,
        `['${findIndex(options, ['value', value])}'].label`,
        parseValueForChip(value)
      )}`,
      color: 'lb',
      textColor: getColor('shark'),
      size: 'medium',
      variant: 'outlined',
      onDelete: () => {
        handleRadioButtonGroupFilterChange(key, false);
      }
    };
  };

  const getChips = () => {
    const search = QueryString.parse(location.search);

    return [
      ...(showOnlyAdditionalChips
        ? []
        : Object.keys(search)
            .reduce((acc, filterKey) => {
              const {
                type,
                options = [],
                disableChips
              } = get(filtersConfig, filterKey, {});
              const locationValue = extractFromSearch(filterKey);
              if (disableChips) {
                return acc;
              }
              switch (type) {
                case 'multi-checkbox':
                  return [
                    ...acc,
                    ...options
                      .map(o => (isObject(o) ? o : { key: o }))
                      .filter(o => search[filterKey].includes(o.key))
                      .map(o => ({
                        label: `${startCase(filterKey)}: ${startCase(o.key)}`,
                        size: 'medium',
                        color: 'lb',
                        textColor: getColor('shark'),
                        variant: 'outlined',
                        onDelete: () => {
                          handleCheckboxFilterChange(filterKey, o.key, false);
                        }
                      }))
                  ];
                case 'multi-tree-dropdown':
                case 'multi-autocomplete':
                  const optionsById = keyBy(options, 'id');
                  const values = locationValue ? locationValue.split(',') : [];
                  return [
                    ...acc,
                    ...values
                      .map(val => parseNumericStr(val))
                      .filter(id => optionsById[id])
                      .map(selectedId => {
                        const { name } = optionsById[selectedId];

                        // disableChip if teams and chip is selectedTeam
                        if (
                          filterBySelectedTeam &&
                          filterKey === teamsFilterKey &&
                          selectedTeam &&
                          selectedId === selectedTeam.id
                        ) {
                          return {};
                        }

                        return {
                          label: `${filterChipLabelMap[filterKey]}: ${name}`,
                          size: 'medium',
                          color: 'lb',
                          textColor: getColor('shark'),
                          variant: 'outlined',
                          onDelete: () => {
                            onListFilterChange(
                              filterKey,
                              values.filter(
                                id => parseNumericStr(id) !== selectedId
                              )
                            );
                          }
                        };
                      })
                  ];
                case 'date':
                  const [from, to] = extractFromSearch(filterKey).split(',');
                  return [
                    ...acc,
                    ...dateFilterToChip(
                      { from, to },
                      filterChipLabelMap[filterKey] || startCase(filterKey),
                      filterKey
                    )
                  ];
                case 'radio':
                  const value = extractFromSearch(filterKey);

                  return [
                    ...acc,
                    compareRadioToChip(value, startCase(filterKey), filterKey)
                  ];

                default:
                  return acc;
              }
            }, [])
            .filter(value => Object.keys(value).length !== 0)),
      ...(additionalCustomChips || [])
    ];
  };

  useEffect(() => {
    if (pageTag) {
      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: pageTag,
        ...(pageTabTag ? { page_tab: pageTabTag } : {})
      });
    }

    const search = QueryString.parse(location.search);

    const searchUpdateDelta = updateInitialSearch(search, filtersConfig);

    setQueryParams(searchUpdateDelta);
  }, []);

  useEffect(() => {
    if (disableGetData) {
      return;
    }

    const { order_by, ...filter } = reqFilterBody;
    setIsLoading(true);

    const filtersPayload = {
      ...(selectedTeam?.id ? { team_ids: [selectedTeam.id] } : {}),
      ...filter
    };

    if (
      selectedTeam?.type === TEAM_TYPES.ALL_CONTRIBUTORS &&
      !sendAllContributorsTeamId
    ) {
      delete filtersPayload.team_ids;
    }

    const getDataPromise = getData({
      order_by,
      filter: filtersPayload,
      offset: pageSize * page,
      limit: pageSize,
      ...(gitScopeToggle !== null
        ? {
            team_git_scope_disabled: !getIsGitScopeByKey(
              gitScopeToggle,
              VIEW_PATH_KEYS.CONTRIBUTORS
            )
          }
        : {})
    });

    if (!getDataPromise.finally) {
      // eslint-disable-next-line no-console
      console.error(
        'getData function should return promise, otherwise loader will not appear'
      );
      setIsLoading(false);
    } else {
      getDataPromise.finally(() => {
        setIsLoading(false);
      });
    }
  }, [reqFilterBody, gitScopeToggle]);

  useEffect(() => {
    const search = QueryString.parse(location.search);

    const { page: prevPage = '0' } = prevSearch;
    const { page: searchPage = '0' } = search;

    const hasPaginationChanged = prevPage !== searchPage;
    const isPaginationOnStart = searchPage === '0';

    if (!hasPaginationChanged && !isPaginationOnStart)
      return setQueryParams({ page: 0 });

    setPrevSearch({ ...search });

    if (!disableChips) {
      const filterChips = getChips();
      setChips(filterChips);
    }

    const newRequestBody = getReqFilterBody();

    if (!isEqual(newRequestBody, reqFilterBody) || hasPaginationChanged) {
      setReqFilterBody(newRequestBody);
    }
  }, [location.search, ...refreshOnDependencies]);

  const mapFilterGroupConfigToRenderedFilterElements = ({
    type,
    ...rest
  }: Record<string, any>) => {
    switch (type) {
      case COMPONENT_TYPE.CUSTOM_FILTER:
        return renderCustomFilter(rest);
      case COMPONENT_TYPE.PREDEFINED:
        return renderPredefined(rest);
      case COMPONENT_TYPE.CHECKBOX:
        return renderCheckboxFilters(rest);
      case COMPONENT_TYPE.RADIO:
        return renderRadioFilters(rest);
      case COMPONENT_TYPE.AUTOCOMPLETE:
        return renderAutocompleteFilters(rest);
      case COMPONENT_TYPE.AUTOCOMPLETE_BY_TYPE:
        return renderAutocompleteByType(rest);
      default:
        return {};
    }
  };

  const additionalFilters = additionalFiltersGroupMap.map(
    mapFilterGroupConfigToRenderedFilterElements
  );
  const filtersGroup = filtersGroupMap.map(
    mapFilterGroupConfigToRenderedFilterElements
  );

  const enabledDateFilters = useMemo(
    () =>
      Object.keys(filtersConfig).filter(key =>
        Object.values(DATE_FILTERS).includes(key as any)
      ) as DateFilter[],
    [filtersConfig]
  );

  const { selectedDateFilter, range } = useDateFilterValue(
    search,
    enabledDateFilters
  );

  const disableGetData = useDisableGetData(
    disableDateFilterLimit,
    range,
    selectedDateFilter
  );

  return (
    <Flex position='relative' wrap='wrap' height='100%'>
      <FiltersPanel>
        <Flex direction='column' height='100%'>
          <DateFilter
            range={range}
            selectedDateFilter={selectedDateFilter}
            onChange={handleDateFilterChange}
            filters={enabledDateFilters}
            filtersConfig={filtersConfig}
          />
          <FiltersSideBar
            filterGroups={filtersGroup}
            additionalFilters={additionalFilters}
            postFiltersSlot={postFiltersSlot}
          />
        </Flex>
      </FiltersPanel>
      <MainSection
        isloading={(isLoading && data.items.length) || isExternalLoading}
        b='0'
      >
        {!disableChips && !!chips.length && (
          <Padding padding='medium' key={chips}>
            <ChipsList chips={chips} />
          </Padding>
        )}

        {preTableSlot}

        {disableGetData ? (
          <InvalidDateRangePlaceholder />
        ) : (
          <>
            {isLoading || isExternalLoading ? (
              <TableSkeleton
                showSkeletonColumnsHead={showSkeletonColumnsHead}
                columns={filteredColumns}
                skeletonColumns={skeletonColumns}
              />
            ) : data.items.length ? (
              // @ts-expect-error
              <Table
                onChangePage={onPageChange}
                onChangeRowsPerPage={onPageSizeChange}
                page={page}
                rowsPerPage={pageSize}
                withPagination
                sortBy={orderByField}
                sortOrder={orderByOrder}
                columns={filteredColumns}
                // @ts-expect-error
                totalRows={data.total}
                data={tableData({ onContRepoTableCellClick })}
                summaryRow={getSummaryRow?.(data)}
                lineHighlightOnHover={lineHighlightOnHover}
                onSort={onSort}
              />
            ) : (
              <>{emptyResultSlot}</>
            )}
          </>
        )}
      </MainSection>
    </Flex>
  );
};

const mapStateToProps = state => {
  const { uiState } = state;

  return {
    uiState,
    currentIteration: currentIterationSelector(state),
    selectedTeam: selectedTeamSelector(state),
    latestIterationEndDate: getLatestIterationEndDateSelector(state),
    latestIteration: getLatestIterationSelector(state),
    contributors: individualContributorsSelector(state)
  };
};

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GridChipFilterContainer);
